// CONTRACT:I4-POLICY-DECISION.1.0
/** The policy bundle source — the SINGLE canonical ABAC model.
 *
 *  ARC 1 (2026-06-25) collapsed the two parallel attribute universes (the stale
 *  `tdfbot.local` SEED that used to live here + the live `defense.local` sealed
 *  bundle) into ONE canonical `defense.local` model. This `SEED_POLICY_BUNDLE` IS
 *  that canonical model — shaped EXACTLY like TDFLite's `/api/policy-attrs` response
 *  (`{namespace, attributes:[{name, rule, values}]}`), so swapping it for a live
 *  fetch + cache stays a one-line change in `loadPolicyBundle()`. The oracle (this
 *  bundle) and the KAS now evaluate the SAME attributes — there is no second universe
 *  to drift from. See docs/AOA-CLASSIFICATION-IDENTITY-MODEL.md §10.
 *
 *  Removed vs the old SEED: `controlled_disclosure_to` (gone — its FVEY/NATO fold into
 *  `releasability`), `department`/`role` (informal → personas metadata, NOT ABAC),
 *  `foreign_release_prohibition` (NOFORN merged into `handling_caveat`). Renamed:
 *  `classification_level`→`classification`, `releasable_to`→`releasability`. Split:
 *  SCI is now `sci_control_system` + `sci_compartment` (kills M8). Added (Fuller CAPCO):
 *  `accm_compartment`, `fgi_constraint`, `atomic_energy`, `cui_category`.
 *
 *  The ONLY load-bearing ordering: classification is TOP_SECRET > SECRET >
 *  CONFIDENTIAL > CUI > UNCLASSIFIED — exported once as `CLASS_ORDER` so every
 *  classification lattice in the codebase imports the SAME ladder (kills M3). */

import type { PolicyBundle } from "./types.ts";

/** THE ONE canonical classification ladder (rank 0 = lowest). Underscore form, CUI
 *  included. Every classification comparison in the system imports THIS map so the
 *  five duplicate (and two BROKEN "TOP SECRET"-with-a-space) lattices can never
 *  diverge again (mis-mapping M3). Hierarchy: TOP_SECRET > SECRET > CONFIDENTIAL >
 *  CUI > UNCLASSIFIED. */
export const CLASS_ORDER: Record<string, number> = {
  UNCLASSIFIED: 0,
  CUI: 1,
  CONFIDENTIAL: 2,
  SECRET: 3,
  TOP_SECRET: 4,
};

/** The canonical classification ladder low→high (inverse of CLASS_ORDER):
 *  CLASS_LEVELS[CLASS_ORDER[x]] === x. Exported for the down-filter / level lists. */
export const CLASS_LEVELS: readonly string[] = [
  "UNCLASSIFIED",
  "CUI",
  "CONFIDENTIAL",
  "SECRET",
  "TOP_SECRET",
] as const;

export const SEED_POLICY_BUNDLE: PolicyBundle = {
  namespace: "defense.local",
  attributes: [
    // HIERARCHY — `values` ordered HIGHEST → lowest (index 0 = TOP_SECRET). Read-down.
    { name: "classification", rule: "hierarchy", values: ["TOP_SECRET", "SECRET", "CONFIDENTIAL", "CUI", "UNCLASSIFIED"] },
    // ALL_OF — subject must hold every bound value. SCI split into control-system + subcompartment.
    { name: "sci_control_system", rule: "allOf", values: ["SI", "TK", "HCS"] },
    { name: "sci_compartment", rule: "allOf", values: ["SI-G", "TK-RUFF", "SI-GAMMA", "ECI"] },
    { name: "sap_program", rule: "allOf", values: ["ALPHA", "BRAVO", "CHARLIE", "WASP", "COBALT", "RAVEN", "TALON"] },
    // Unified dissemination/handling caveats. NOFORN merged in here; REL_TO dropped (M2/M4).
    // EXDIS/NODIS (State-cable controls) intentionally not modeled in this bundle.
    { name: "handling_caveat", rule: "allOf", values: ["NOFORN", "ORCON", "PROPIN", "IMCON", "RELIDO", "FOUO", "LIMDIS"] },
    // ANY_OF — subject must hold at least one bound value. FVEY/NATO + NATO members +
    // CENTCOM-AOR partner trigraphs are DIRECT releasability values now (M1: no second axis).
    {
      name: "releasability",
      rule: "anyOf",
      values: [
        "USA", "GBR", "CAN", "AUS", "NZL", "FVEY", "NATO",
        "ALB", "BEL", "BGR", "HRV", "CZE", "DNK", "EST", "FIN", "FRA", "DEU", "GRC", "HUN",
        "ISL", "ITA", "LVA", "LTU", "LUX", "MNE", "NLD", "MKD", "NOR", "POL", "PRT", "ROU",
        "SVK", "SVN", "ESP", "SWE", "TUR",
        "AFG", "BHR", "EGY", "IRQ", "JOR", "KAZ", "KWT", "KGZ", "LBN", "OMN", "PAK", "QAT",
        "SAU", "TJK", "TKM", "ARE", "UZB", "YEM",
      ],
    },
    // Fuller formal CAPCO attrs (allOf).
    { name: "accm_compartment", rule: "allOf", values: ["ACCM-SANDSTORM", "ACCM-IRONCLAD"] },
    { name: "fgi_constraint", rule: "allOf", values: ["FGI-GBR", "FGI-DEU", "FGI-AUS", "FGI-NATO"] },
    { name: "atomic_energy", rule: "allOf", values: ["RD", "FRD", "CNWDI", "DCNI"] },
    { name: "cui_category", rule: "allOf", values: ["SP-CTI", "SP-PRVCY", "SP-LEI"] },
  ],
};

/** Load the policy bundle. TODAY: returns the verified canonical seed (no network).
 *  LATER: `const r = await fetch(`${base}/api/policy-attrs`); return r.json();`
 *  — same shape (now the SAME namespace too), with a cache + TTL (rules change rarely).
 *  Kept async so the call site never changes when we wire the live fetch. */
export async function loadPolicyBundle(_opts?: { fetchBase?: string }): Promise<PolicyBundle> {
  return SEED_POLICY_BUNDLE;
}
